/**
 * Plain-language help text and examples for Pricing Settings fields.
 * Shown in the (i) info bubbles on Settings → Pricing.
 */

/**
 * @typedef {object} PricingFieldHelp
 * @property {string} title
 * @property {string} summary - One-line plain summary
 * @property {string[]} details - Longer explanation paragraphs
 * @property {string} [example] - Concrete example value or scenario
 * @property {string} [whenApplies] - When this rule/charge kicks in
 */

/** @type {Record<string, PricingFieldHelp>} */
export const PRICING_FIELD_HELP = Object.freeze({
  "pricing.automation.enabled": {
    title: "Enable automated pricing",
    summary:
      "Should the system draft a customer price after a lead is ready, or wait for a person to price it?",
    details: [
      "OFF = staff type the quote themselves. The lead is still captured.",
      "ON = the system calculates a draft total. A person still reviews it before the customer sees it.",
      "Turn this on only after the money amounts below are filled in (local base, or truck prices if truck-led is on).",
    ],
    example:
      "Leave OFF while you set Local base to R2 500. Then turn ON. New leads get a draft total instead of a blank quote.",
    whenApplies: "Every new lead that goes through automated quote preparation.",
  },
  "pricing.automation.truckLedLocal.enabled": {
    title: "Truck-led same-province pricing",
    summary:
      "Same-province exclusive moves: charge by truck size, or one flat local price for everyone?",
    details: [
      "OFF = every local exclusive move starts at the same Local base amount (e.g. R2 500).",
      "ON = the system picks the smallest truck that fits the load. That truck’s baseAmount is the starting price.",
      "If the trip is longer than Included km (still same province), extra km are added on top of the truck price.",
      "Keep this OFF until vehicle bands have both a size (m³) and a baseAmount, and a few test quotes look right.",
    ],
    example:
      "OFF: 8 m³ Sandton → Midrand = R2 500 flat.\nON: same 8 m³ load fits the 1-ton truck → customer starts at that truck’s R2 500 base.",
    whenApplies:
      "Same-province exclusive moves only (local or intra-province). Shared long-distance and other-province exclusive are not priced this way.",
  },
  "pricing.policy.version": {
    title: "Policy version",
    summary:
      "A label saved on each quote so you can later see which pricing rules created it.",
    details: [
      "This is not a price. Think of it as a document version, like “v3”.",
      "When you change important pricing rules, bump this label (e.g. w15-e02-v1 → w15-e02-v2).",
      "Old quotes keep the old label. New quotes get the new one.",
    ],
    example: "w15-e02-v1 — later you can filter “which quotes used v1?”",
  },
  "pricing.local.maxDistanceKm": {
    title: "Local boundary (km, legacy)",
    summary:
      "Backup distance used only if Included km is empty.",
    details: [
      "Prefer Included km. That is the setting the new same-province split uses.",
      "If Included km is blank, the system falls back to this number.",
      "Typical value: 50 km.",
    ],
    example:
      "50 — used only when Included km is not set. If Included km is 50, this backup is ignored.",
    whenApplies: "Fallback for “is this still a local job?” when Included km is missing.",
  },
  "pricing.local.includedKm": {
    title: "Included km (same province)",
    summary:
      "How far a same-province move can go before we add extra km on top of the truck (or flat) base.",
    details: [
      "Inside this distance, same province: customer pays the truck/flat base + access extras. No km add-on.",
      "Past this distance, still same province: same base + extra km × Intra-province rate.",
      "Different provinces do not use this. Those go to corridor (shared) or stay manual (exclusive).",
    ],
    example:
      "Set to 50 km.\n• 35 km Sandton → Midrand = local. No extra km charge.\n• 80 km same province = truck base + 30 extra km × the intra-province rate.",
    whenApplies:
      "Compared with the route km when pickup and delivery are in the same province.",
  },
  "pricing.local.defaultService": {
    title: "Default local service",
    summary:
      "What kind of local move we assume unless someone changes it on the lead.",
    details: [
      "exclusive_load = dedicated truck / not sharing space with another customer.",
      "shared_load = space on a consolidated truck (used for many long-distance / corridor jobs).",
      "Leave as exclusive_load unless commercial says local jobs should default to shared.",
    ],
    example: "exclusive_load — a typical Joburg local house move.",
  },
  "pricing.local.baseAmount": {
    title: "Local base amount (ZAR)",
    summary:
      "The starting customer price for a standard local exclusive move when truck-led pricing is OFF.",
    details: [
      "This is the “everyone pays at least this” local number — e.g. R2 500.",
      "When truck-led is ON, same-province jobs use each truck’s own baseAmount instead. This flat amount is then only a backup for older paths.",
      "Must be greater than zero before automation can produce real totals (unless truck-led bands already have prices).",
    ],
    example:
      "2500 — with truck-led OFF, a 20 km local move starts at R2 500, then stairs/long-carry can add on top.",
    whenApplies: "Local exclusive moves when truck-led is OFF.",
  },
  "pricing.intraProvince.ratePerKm": {
    title: "Intra-province rate (ZAR/km)",
    summary:
      "Rand per extra kilometre for same-province jobs that go further than Included km.",
    details: [
      "Extra km = route km minus Included km. Charge = extra km × this rate.",
      "This sits on top of the truck (or flat) base. It does not replace the base.",
      "Needed when truck-led is ON and the job is same-province but longer than Included km. If the rate is 0 or blank, those quotes go to manual review.",
    ],
    example:
      "Rate R15/km, Included km = 50, route = 80 km.\nExtra = 30 km × R15 = R450 distance line, plus the truck base.",
    whenApplies:
      "Same province, route longer than Included km, truck-led ON.",
  },
  "pricing.longDistance.ratePerM3Km": {
    title: "Shared long-distance rate (ZAR per m³·km)",
    summary:
      "Legacy formula for shared long-distance jobs (volume × extra km). Used only when corridor pricing is OFF. Demoted from primary Pricing sections.",
    details: [
      "Formula: cubic metres × kilometres past the local boundary × this rate.",
      "Only for shared_load jobs beyond the local boundary.",
      "When corridor pricing is ON, ignore this for interprovince shared jobs — the corridor catalogue wins.",
      "Not shown as a first-class Pricing group; engine still loads the key for corridor-off back-compat.",
    ],
    example:
      "Rate R12, load 8 m³, trip 120 km, boundary 50 km.\nExtra km = 70.\n8 × 70 × 12 = R6 720 variable charge.",
    whenApplies:
      "Shared-load, route longer than the local boundary, corridor pricing OFF.",
  },
  "pricing.inventory.packingFactor": {
    title: "Packing factor (deprecated)",
    summary:
      "Old leftover. Demoted from Pricing UI. Packing is sold as separate products (boxes, wrap, labour).",
    details: [
      "This used to multiply the transport price when packing was needed.",
      "The engine now ignores it. Leave it at 1 in the database if present.",
      "Sell packing from the product catalogue instead.",
    ],
    example: "1 — ignored. A TV box is a catalogue line, not a 1.2× transport markup.",
    whenApplies: "No longer applied to transport.",
  },
  "pricing.inventory.nonStackableFactor": {
    title: "Non-stackable factor",
    summary:
      "Extra billable volume when items cannot be stacked (pianos, glass, “do not stack”).",
    details: [
      "Those items take more truck space than their raw cube.",
      "1.15 means “count 15% more volume” for pricing. It is not a separate packing product.",
      "Only used when inventory is flagged as non-stackable.",
    ],
    example:
      "1.15 on a 10 m³ load that cannot be stacked → we price as 11.5 m³.",
    whenApplies: "When non-stackable is flagged on inventory lines.",
  },
  "pricing.corridors.enabled": {
    title: "Enable corridor pricing (live)",
    summary:
      "Other-province shared loads: use Bubakii’s corridor catalogue (per m³ or minimum), instead of the old m³ × km formula.",
    details: [
      "OFF = shared long-distance uses the legacy m³·km rate (still loadable; not shown as a primary Pricing section).",
      "ON = JHB→CPT style shared jobs use the corridor catalogue: the higher of (minimum) or (m³ × rate per m³).",
      "Same-province jobs never use corridors.",
      "Exclusive other-province jobs stay manual for now (no auto exclusive corridor price).",
      "Stairs, long-carry, and packing stay as separate lines.",
      "If the route has no matching corridor row, the quote goes to a person — we do not silently fall back to m³·km.",
    ],
    example:
      "ON + a GP–WC row at R390/m³ (min R1 500).\nA shared JHB→CPT 20 m³ job: transport = 20 × 390 = R7 800 (above the minimum).",
    whenApplies:
      "Shared-load moves where pickup and delivery provinces are different.",
  },
  "pricing.corridors.catalogue": {
    title: "Corridor pricing catalogue",
    summary:
      "Bubakii price list for shared moves on commercial corridors: a minimum, plus a rate per cubic metre.",
    details: [
      "Each row is an originRegion → destinationRegion pair (commercial pricing regions; default = SA provinces), usually serviceType shared_load.",
      "Customer transport = whichever is bigger: the minimum, or billable m³ × ratePerM3.",
      "Directed rows (GP→KZN vs KZN→GP) are preferred when both exist.",
      "Optional estimatedProviderCostPerM3 is internal only — what you think you’ll pay the mover per m³. Customers never see it.",
      "Reserved optional fields direction, season, mileageAdjustment are ignored by the calculator until activated later.",
      "Do not paste competitor rate cards into this catalogue as production prices.",
      "If this list is empty while corridor pricing is ON, interprovince shared quotes cannot auto-price.",
    ],
    example:
      "GP–WC, minimum R1 500, R390 per m³:\n• 3 m³ → R1 500 (minimum wins)\n• 20 m³ → R7 800 (20 × 390)\n\nJSON shape:\n[{\"id\":\"GP-WC\",\"originRegion\":\"GP\",\"destinationRegion\":\"WC\",\"serviceType\":\"shared_load\",\"minimumAmount\":1500,\"ratePerM3\":390,\"enabled\":true}]",
    whenApplies:
      "When corridor pricing is ON and we can resolve origin/destination commercial regions (province fallback when the location map is empty).",
  },
  "pricing.corridors.locationRegionMap": {
    title: "Location → pricing region map",
    summary:
      "Optional rules that map a city, suburb, town, postal code, or hub to a commercial pricing region.",
    details: [
      "Empty (default) = every stop’s pricing region falls back to its SA province.",
      "matchType: city | suburb | town | postalCode | hub.",
      "Does not change product routing — provinces still decide local vs interprovince.",
      "Use this when a corridor should be finer than province-to-province without inventing city-to-city rate rows.",
    ],
    example:
      "[{\"matchType\":\"city\",\"matchValue\":\"George\",\"regionCode\":\"WC\",\"active\":true}]",
    whenApplies:
      "When corridor pricing is ON and you need address-level region resolution beyond province codes.",
  },
  "pricing.history.reviewVariancePercent": {
    title: "Historical variance threshold (%)",
    summary:
      "How far the new price may sit from similar past jobs before we ask a person to check.",
    details: [
      "The system looks at similar completed / deposit-paid jobs as a sense-check. It does not replace your formula.",
      "If the new price is this % higher or lower than that benchmark, the quote is flagged for review.",
      "Typical value: 20%.",
    ],
    example:
      "20%.\nNew quote R10 000 vs similar past jobs around R7 000 (about 43% higher) → flagged for a person to check.",
    whenApplies: "When we have comparable past jobs to compare against.",
  },
  "pricing.quotes.expiryDays": {
    title: "Provisional quote expiry (days)",
    summary:
      "How many days a draft auto-quote stays valid before it should be refreshed.",
    details: [
      "This is the draft / provisional window — not the same as a sent quote’s negotiation deadline.",
      "Shorter = less chance of a stale price. Longer = customer has more time.",
    ],
    example:
      "7 — created Monday 11 Aug → treat as expired Monday 18 Aug unless someone refreshes it.",
  },
  "pricing.access.longCarry.thresholdMetres": {
    title: "Long-carry threshold (m)",
    summary:
      "How far the crew can walk from truck to door before the long-carry fee kicks in.",
    details: [
      "Measured in metres at pickup or delivery.",
      "Further than this number → add the Long carry (ZAR) amount at that stop.",
      "Typical value: 20 metres.",
    ],
    example:
      "20 m.\nCustomer’s door is 35 m from where the truck can park → long-carry fee at that stop.",
    whenApplies: "Pickup or delivery walking distance is greater than this threshold.",
  },
  "pricing.resources.additionalTruckAmount": {
    title: "Additional truck charge (ZAR)",
    summary: "Fixed fee for each extra truck a person confirms, on top of the first truck.",
    details: [
      "Used when the load does not fit one truck.",
      "Not added automatically — an admin must confirm the extra trucks.",
    ],
    example: "R1 500 per extra truck.\nTwo extra trucks confirmed → + R3 000.",
    whenApplies: "When admin confirms additional trucks on the move.",
  },
  "pricing.resources.additionalTripAmount": {
    title: "Additional trip charge (ZAR)",
    summary: "Fixed fee for each extra trip (second run, shuttle) a person confirms.",
    details: [
      "Covers coming back to finish the load, or a shuttle run.",
      "Only priced after admin confirmation.",
    ],
    example: "R800.\nOne extra trip confirmed → + R800.",
    whenApplies: "When admin confirms extra trips.",
  },
  "pricing.resources.specialUnitAmount": {
    title: "Special resource unit charge (ZAR)",
    summary:
      "Fixed fee per specialist unit (hoist, extra equipment) when admin confirms it.",
    details: [
      "For extras that are not “another truck” or “another trip”.",
      "Set this before those units can auto-price.",
    ],
    example: "R500 each.\nTwo hoist units confirmed → + R1 000.",
    whenApplies: "When admin confirms special units.",
  },
  "pricing.access.stairs.fixedAmount": {
    title: "Stairs access (ZAR)",
    summary:
      "Fixed fee when the crew must use stairs at pickup or delivery (walk-up, no usable lift).",
    details: [
      "Charged once per stop where stairs apply.",
      "We do not also charge Restricted lift for the same path unless there is a documented reason.",
    ],
    example:
      "R250.\nGround-floor pickup, 2nd-floor walk-up delivery → + R250 at delivery only.",
    whenApplies: "Stairs flagged at pickup and/or delivery.",
  },
  "pricing.access.restrictedLift.fixedAmount": {
    title: "Restricted lift (ZAR)",
    summary:
      "Fixed fee when there is a lift, but it cannot be used normally (too small, booked slots, out of order).",
    details: [
      "Covers extra time when the lift does not really help.",
      "Different from stairs. The system picks the most specific access rule so we do not double-charge the same problem.",
    ],
    example:
      "R200.\nLift exists but a fridge will not fit → restricted-lift fee at that stop.",
    whenApplies: "Restricted or unusable lift at pickup or delivery.",
  },
  "pricing.access.longCarry.fixedAmount": {
    title: "Long carry (ZAR)",
    summary:
      "The rand amount added when walking distance is longer than the long-carry threshold.",
    details: [
      "The threshold (metres) decides if it applies. This setting is the money.",
      "Can apply at pickup, delivery, or both.",
    ],
    example:
      "R300, threshold 20 m.\n40 m carry at pickup → + R300 at pickup. Delivery is 10 m → no long-carry there.",
    whenApplies: "Walking distance greater than the long-carry threshold at that stop.",
  },
  "pricing.access.difficultVehicleAccess.fixedAmount": {
    title: "Difficult vehicle access (ZAR)",
    summary:
      "Fixed fee when the truck cannot reach a normal loading point (narrow road, low boom, steep driveway).",
    details: [
      "This is about the truck’s path, not how far the crew walks.",
      "We do not stack this with long-carry or stairs for the same issue — the most specific rule wins.",
    ],
    example:
      "R350.\nSteep driveway, truck parks in the street → difficult access at delivery.",
    whenApplies: "Difficult vehicle access flagged at pickup or delivery.",
  },
  "pricing.access.noLoadingZone.fixedAmount": {
    title: "No loading zone (ZAR)",
    summary:
      "Fixed fee when there is no legal place to park/load near the door (CBD, strict estate).",
    details: [
      "Typical when the truck needs a permit or must wait far from the entrance.",
      "Charged per stop where it is flagged.",
    ],
    example: "R200.\nNo loading bay at a Sandton tower pickup → + R200 at pickup.",
    whenApplies: "No loading zone flagged at pickup or delivery.",
  },
  "pricing.adjustments.approvalMode": {
    title: "Ad-hoc adjustment approval",
    summary:
      "When staff add a one-off discount or surcharge, must a manager approve it before send?",
    details: [
      "Leave as always — every manual extra or discount needs explicit approval.",
      "Do not change this unless a future policy allows small auto-approved tweaks.",
    ],
    example:
      "always — agent knocks R400 off a quote → it cannot go to the customer until an admin approves.",
  },
  "pricing.rounding.increment": {
    title: "Rounding increment (ZAR)",
    summary:
      "Draft totals are rounded to the nearest this many rand, so quotes look clean.",
    details: [
      "Typical: nearest R10.",
      "Halfway cases round up (R2 545 with increment 10 → R2 550).",
    ],
    example: "10.\nCalculated R2 547 → customer total R2 550.",
  },
  "pricing.tax.vatApplicable": {
    title: "Apply VAT",
    summary: "Whether 15% VAT is added on top of the draft customer total.",
    details: [
      "Today this stays OFF for automated provisional removal quotes.",
      "Turn ON only when finance says VAT must appear on these draft totals.",
    ],
    example: "OFF — quote shows R10 000, not R10 000 + 15% VAT.",
  },
  "pricing.vehicles.bands": {
    title: "Vehicle capacity + truck bases",
    summary:
      "Your truck menu: how much each truck holds, what customers see, who is included, and the exclusive starting price.",
    details: [
      "size = internal name (1_ton, 4_ton). customerLabel = what the customer sees (Small Move).",
      "usableM3 = real space in the truck. maxBillableM3 = optional “stop picking this truck after this many billable m³”.",
      "baseAmount = starting exclusive price when truck-led is ON.",
      "includedCrew = drivers + helpers that come with that truck. Saved for dispatch — not an extra customer line.",
      "The system picks the smallest truck that still fits. If the load is bigger than every truck → person reviews (may need two trucks).",
    ],
    example:
      "8 m³ load, truck-led ON.\n1-ton band: max billable 10 m³, base R2 500 → this truck is picked. Customer starts at R2 500.\n\nJSON shape:\n[{\"size\":\"1_ton\",\"usableM3\":12,\"maxBillableM3\":10,\"customerLabel\":\"Small Move\",\"label\":\"1 Ton\",\"baseAmount\":2500,\"includedCrew\":{\"drivers\":1,\"helpers\":1}}]",
    whenApplies:
      "After we know the load size. Truck-led uses baseAmount as the same-province transport start.",
  },
  "pricing.economics.estimatedProviderCostPercent": {
    title: "Estimated provider cost (% of quote)",
    summary:
      "Fallback mover-cost guess when commercial split did not set a driver offer. Staff only — never on the customer PDF.",
    details: [
      "0 = do not guess.",
      "70 means: if the customer quote is R10 000, assume mover cost about R7 000.",
      "If a corridor row has estimatedProviderCostPerM3, that wins for that route.",
      "Mainly used when split is OFF, or exclusive LD has no automated total yet.",
    ],
    example: "70% on a R10 000 quote → estimated mover cost R7 000.",
    whenApplies: "Internal profitability when no commercial-split provider offer.",
  },
  "pricing.economics.split.enabled": {
    title: "Enable commercial split",
    summary:
      "Charge the customer cost + markup, and advertise to drivers at cost minus contribution.",
    details: [
      "Applies to local, intra-province, shared-load, and exclusive LD when an automated total exists.",
      "Example: cost R10 000, markup 20%, contribution 10% → customer R12 000, driver R9 000, Bubakii R3 000.",
      "Shared-load also never charges below the shared minimum (default R2 500).",
      "Skipped when exclusive LD is still policy-pending (no customer total).",
    ],
    example:
      "Cost R10 000 → customer R12 000, advertised R9 000, Bubakii keeps R3 000.",
    whenApplies: "After transport, access, and packing on an automated quote.",
  },
  "pricing.economics.customerMarkup.percent": {
    title: "Customer markup %",
    summary: "Percent of job cost added to what the customer pays.",
    details: [
      "This is a markup on cost, not a leftover % of the customer price.",
      "20% on R10 000 cost → customer R12 000.",
      "Applies to local, intra-province, shared-load, and exclusive LD (with automated total).",
    ],
    example: "20% × R10 000 = R2 000 on the customer quote.",
    whenApplies: "When commercial split is ON.",
  },
  "pricing.economics.contribution.percent": {
    title: "Contribution %",
    summary:
      "Percent of job cost taken off the advertised driver amount while the provider cut is on.",
    details: [
      "Drivers do not see the customer total. They see cost minus this cut.",
      "After the provider-cut end date, advertise full cost; Bubakii keeps markup only.",
    ],
    example: "10% × R10 000 = R1 000 off the driver offer.",
    whenApplies: "When commercial split and provider cut are ON.",
  },
  "pricing.economics.contribution.minAmountPerJob": {
    title: "Contribution minimum",
    summary: "Smallest Rand cut from the driver offer if the % would be tiny.",
    details: ["0 means percent only."],
    example: "Min R500 on a R3 000 job (10% = R300) → cut R500, advertise R2 500.",
    whenApplies: "When commercial split and provider cut are ON.",
  },
  "pricing.economics.shared.minCustomerTotal": {
    title: "Shared-load customer minimum",
    summary: "Never quote a shared-load customer below this amount.",
    details: [
      "Applied after markup. The driver offer stays on cost minus contribution.",
      "Does not apply to local, intra-province, or exclusive LD.",
    ],
    example:
      "Shared cost R1 800 → 20% = R2 160, so customer becomes R2 500. Driver still sees R1 620.",
    whenApplies: "Shared-load (other province) only, when commercial split is ON.",
  },
  "pricing.economics.providerCut.enabled": {
    title: "Cut contribution from the driver offer",
    summary: "Take contribution off what we advertise to drivers.",
    details: [
      "ON = advertise cost − contribution.",
      "OFF = advertise full cost. Customer still pays cost + markup.",
    ],
    example: "OFF on a R10 000 job → advertise R10 000, customer R12 000, Bubakii R2 000.",
    whenApplies: "When commercial split is ON.",
  },
  "pricing.economics.providerCut.until": {
    title: "Provider cut end date",
    summary:
      "Last day the contribution haircut applies. After that, advertise full cost again.",
    details: [
      "Use YYYY-MM-DD. The end date is included.",
      "Each quote stores the driver amount when it is priced.",
    ],
    example: "2027-01-11 → jobs priced on that day still get the cut; 12 Jan does not.",
    whenApplies: "When commercial split and provider cut are ON.",
  },
});

/** @type {Record<string, { summary: string, details?: string[], example?: string }>} */
export const PRICING_SECTION_HELP = Object.freeze({
  automation: {
    summary:
      "Master switches: should the system draft prices, and which rule-set label is saved on each quote?",
    details: [
      "Start with automation OFF. Fill in the money amounts. Check a few test leads. Then turn automation ON.",
      "Leave truck-led OFF until vehicle bands have sizes and prices that look right on real quotes.",
    ],
    example:
      "Week 1: automation OFF, set local base R2 500. Week 2: turn automation ON for new leads.",
  },
  local: {
    summary:
      "Same-province nearby jobs: how many km are included, and the flat starting price if truck-led is off.",
    details: [
      "Included km is the “free distance” inside the truck/flat base.",
      "The flat Local base amount is used only when truck-led is OFF.",
    ],
    example:
      "Included 50 km + flat base R2 500: a 30 km local exclusive move starts at R2 500.",
  },
  intraProvince: {
    summary:
      "Same province, but further than Included km: keep the truck/flat base, then add extra km × this rate.",
    details: [
      "When truck-led is ON this rate must be greater than zero, or those longer same-province quotes go to review.",
    ],
    example:
      "80 km same province, 50 included, R15/km → + R450 on top of the truck base.",
  },
  longDistance: {
    summary:
      "Legacy shared long-distance (m³ × extra km). Demoted from primary Pricing sections — used only when corridor pricing is OFF.",
    details: [
      "Prefer Corridor / shared long-distance for interprovince shared loads.",
      "This key remains loadable for backward compatibility; it is not a first-class ops section.",
    ],
    example:
      "Corridors OFF: 8 m³ × 70 extra km × R12 = R6 720 variable line.",
  },
  corridors: {
    summary:
      "Bubakii shared other-province rate card: minimum + rate per m³ from a commercial region-pair catalogue.",
    details: [
      "First-class Bubakii group alongside local, intra-province, vehicles, and access — not competitor categories (Dedicated LD, Peak, Depo-to-Depo, etc.).",
      "Turn the flag ON and keep the catalogue filled in. A missing pair → person reviews the quote.",
      "Exclusive other-province jobs stay manual. Same-province jobs never use this list.",
    ],
    example:
      "GP–WC R390/m³, min R1 500: 20 m³ shared JHB→CPT transport = R7 800.",
  },
  access: {
    summary:
      "Site adjustments: stairs, long walk, no loading bay, tricky truck access.",
    details: [
      "Each condition has its own amount. It can apply at pickup, delivery, or both.",
      "If two conditions describe the same problem, we charge the most specific one — not both.",
    ],
    example:
      "Stairs at delivery R250 + long carry at pickup R300 = R550 access extras.",
  },
  resources: {
    summary:
      "Extra trucks, extra trips, or specialist kit — only after a person confirms they are needed.",
    details: [
      "These never auto-add from volume alone. Admin confirmation is required.",
    ],
    example: "One extra truck at R1 500 + one extra trip at R800 = + R2 300.",
  },
  inventory: {
    summary:
      "How volume is adjusted for non-stackable loads. Packing is sold as catalogue products.",
    details: [
      "Use the non-stackable factor for “cannot stack” loads. Packing factor is demoted / ignored.",
    ],
    example: "1.15 non-stackable on 10 m³ → we price 11.5 m³.",
  },
  policy: {
    summary:
      "House rules: when a person must review, how long a draft lasts, rounding, VAT, and approval of one-off changes.",
    example:
      "20% history variance + 7-day expiry + round to R10 + VAT off.",
  },
  vehicles: {
    summary:
      "The truck menu used to pick a size and, if truck-led is ON, the same-province starting price.",
    details: [
      "Customers see labels like Small Move. Ops see 1-ton / 4-ton and included crew.",
    ],
    example:
      "8 m³ → Small Move (1-ton), base R2 500 when truck-led is ON.",
  },
  economics: {
    summary:
      "Staff-only commercial split. Customers never see these numbers or line labels.",
    details: [
      "When split is ON: customer pays cost + markup; drivers are advertised cost − contribution.",
      "Applies to local, intra, shared, and exclusive LD with an automated total.",
      "Shared-load customer minimum is shared-only.",
      "Estimated provider % is a fallback when split did not set a driver offer.",
    ],
    example:
      "Cost R10 000, markup 20%, contribution 10% → customer R12 000, driver R9 000, Bubakii R3 000.",
  },
});

/**
 * @param {string} key
 * @returns {PricingFieldHelp|null}
 */
export function getPricingFieldHelp(key) {
  return PRICING_FIELD_HELP[key] || null;
}

/**
 * @param {string} sectionKey
 * @returns {{ summary: string, details?: string[], example?: string }|null}
 */
export function getPricingSectionHelp(sectionKey) {
  return PRICING_SECTION_HELP[sectionKey] || null;
}
